/**
 * @fileoverview Device Management Service - Manages primary and linked devices for WebAuthn
 *
 * This service implements the device proxy model where:
 * - Primary devices store the master encryption key (encrypted with WebAuthn)
 * - Linked devices forward decryption requests to primary devices
 * - Multiple primary devices supported for redundancy (2-of-M model)
 * - QR code-based device linking for seamless cross-device setup
 *
 * **Device Types:**
 * - Primary: Has encrypted copy of master key, can decrypt independently
 * - Linked: No keys, forwards requests to primary via encrypted channel
 *
 * **Architecture Pattern (WhatsApp Model):**
 * - iPhone (primary) → stores master key
 * - Desktop (linked) → requests decryption from iPhone
 * - iPad (linked) → requests decryption from iPhone
 * - Home PC (primary) → also stores master key (redundancy)
 *
 * @module services/DeviceManagementService
 * @requires @xanectech/supabase-client
 * @requires @xanectech/lib
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger, isRecord } from '@xanectech/lib';
import type {
  Device,
  DeviceType,
  DeviceStatus,
  DeviceConnection,
  QRCodePayload,
} from '@xanectech/types';

const logger = createLogger('DeviceManagementService');

// ============================================================================
// Device Registration
// ============================================================================

/**
 * Register a new primary device
 *
 * Primary devices store the master encryption key (encrypted with WebAuthn)
 * and can decrypt data independently without requiring other devices.
 *
 * @param userId - User ID
 * @param deviceName - Human-readable device name
 * @param deviceInfo - Device platform and browser info
 * @param credentialId - WebAuthn credential ID
 * @returns Registered device details
 */
export async function registerPrimaryDevice(
  userId: string,
  deviceName: string,
  deviceInfo: Device['deviceInfo'],
  credentialId: string,
): Promise<Device> {

  try {
    const deviceData = {
      user_id: userId,
      device_type: 'primary',
      device_name: deviceName,
      device_info: deviceInfo,
      credential_id: credentialId,
      status: 'active',
    };

    const { data, error } = await supabase
      .from('encryption_devices')
      .insert(deviceData)
      .select()
      .single();

    if (error) {
      logger.error('Failed to register primary device', error);
      throw new Error(`Device registration failed: ${error.message}`);
    }

    logger.info('Primary device registered successfully', {
      deviceId: data.id,
    });

    return mapDeviceFromDB(data);
  } catch (error) {
    logger.error('Failed to register primary device', error as Error);
    throw error;
  }
}

/**
 * Register a new linked device
 *
 * Linked devices do not store encryption keys. They forward decryption
 * requests to primary devices via encrypted channels.
 *
 * @param userId - User ID
 * @param deviceName - Human-readable device name
 * @param deviceInfo - Device platform and browser info
 * @param primaryDeviceId - ID of primary device to link to
 * @returns Registered linked device details
 */
export async function registerLinkedDevice(
  userId: string,
  deviceName: string,
  deviceInfo: Device['deviceInfo'],
  primaryDeviceId: string,
): Promise<{ device: Device; connection: DeviceConnection }> {

  try {
    // Register the linked device
    const deviceData = {
      user_id: userId,
      device_type: 'linked',
      device_name: deviceName,
      device_info: deviceInfo,
      status: 'active',
    };

    const { data: deviceRecord, error: deviceError } = await supabase
      .from('encryption_devices')
      .insert(deviceData)
      .select()
      .single();

    if (deviceError) {
      logger.error('Failed to register linked device', deviceError);
      throw new Error(
        `Linked device registration failed: ${deviceError.message}`,
      );
    }

    // Create the device connection
    const connectionData = {
      primary_device_id: primaryDeviceId,
      linked_device_id: deviceRecord.id,
      connection_status: 'active',
    };

    const { data: connectionRecord, error: connectionError } = await supabase
      .from('device_connections')
      .insert(connectionData)
      .select()
      .single();

    if (connectionError) {
      logger.error('Failed to create device connection', connectionError);
      // Clean up the device record if connection fails
      await supabase
        .from('encryption_devices')
        .delete()
        .eq('id', deviceRecord.id);
      throw new Error(`Device connection failed: ${connectionError.message}`);
    }

    logger.info('Linked device registered successfully', {
      deviceId: deviceRecord.id,
      primaryDeviceId,
    });

    return {
      device: mapDeviceFromDB(deviceRecord),
      connection: mapConnectionFromDB(connectionRecord),
    };
  } catch (error) {
    logger.error('Failed to register linked device', error as Error);
    throw error;
  }
}

// ============================================================================
// Device Listing and Management
// ============================================================================

/**
 * List all devices for a user
 *
 * @param userId - User ID
 * @returns Array of user's devices (primary and linked)
 */
export async function listUserDevices(userId: string): Promise<Device[]> {

  try {
    const { data, error } = await supabase
      .from('encryption_devices')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to list devices', error);
      throw new Error(`Failed to list devices: ${error.message}`);
    }

    const devices = (data || []).map(mapDeviceFromDB);
    return devices;
  } catch (error) {
    logger.error('Failed to list devices', error as Error);
    throw error;
  }
}

/**
 * List only primary devices for a user
 *
 * @param userId - User ID
 * @returns Array of primary devices
 */
export async function listPrimaryDevices(userId: string): Promise<Device[]> {

  try {
    const { data, error } = await supabase
      .from('encryption_devices')
      .select('*')
      .eq('user_id', userId)
      .eq('device_type', 'primary')
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to list primary devices', error);
      throw new Error(`Failed to list primary devices: ${error.message}`);
    }

    const devices = (data || []).map(mapDeviceFromDB);
    return devices;
  } catch (error) {
    logger.error('Failed to list primary devices', error as Error);
    throw error;
  }
}

/**
 * Get device by ID
 *
 * @param deviceId - Device ID
 * @returns Device details
 */
export async function getDevice(deviceId: string): Promise<Device | null> {

  try {
    const { data, error } = await supabase
      .from('encryption_devices')
      .select('*')
      .eq('id', deviceId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return null;
      }
      logger.error('Failed to get device', error);
      throw new Error(`Failed to get device: ${error.message}`);
    }

    return mapDeviceFromDB(data);
  } catch (error) {
    logger.error('Failed to get device', error as Error);
    throw error;
  }
}

/**
 * Revoke a device (deactivate)
 *
 * @param deviceId - Device ID to revoke
 */
export async function revokeDevice(deviceId: string): Promise<void> {

  try {
    const { error } = await supabase
      .from('encryption_devices')
      .update({ status: 'revoked', updated_at: new Date().toISOString() })
      .eq('id', deviceId);

    if (error) {
      logger.error('Failed to revoke device', error);
      throw new Error(`Failed to revoke device: ${error.message}`);
    }

    // Also disconnect any linked devices
    await supabase
      .from('device_connections')
      .update({ connection_status: 'disconnected' })
      .or(`primary_device_id.eq.${deviceId},linked_device_id.eq.${deviceId}`);

    logger.info('Device revoked successfully', { deviceId });
  } catch (error) {
    logger.error('Failed to revoke device', error as Error);
    throw error;
  }
}

/**
 * Promote a linked device to primary
 *
 * This allows a previously linked device to become independent with its own
 * copy of the master encryption key.
 *
 * @param deviceId - Linked device ID to promote
 * @param credentialId - WebAuthn credential ID for the device
 */
export async function promoteToPrimary(
  deviceId: string,
  credentialId: string,
): Promise<Device> {

  try {
    const { data, error } = await supabase
      .from('encryption_devices')
      .update({
        device_type: 'primary',
        credential_id: credentialId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', deviceId)
      .select()
      .single();

    if (error) {
      logger.error('Failed to promote device', error);
      throw new Error(`Failed to promote device: ${error.message}`);
    }

    logger.info('Device promoted to primary', { deviceId });
    return mapDeviceFromDB(data);
  } catch (error) {
    logger.error('Failed to promote device', error as Error);
    throw error;
  }
}

// ============================================================================
// Device Connections
// ============================================================================

/**
 * Get device connections for a user
 *
 * @param userId - User ID
 * @returns Array of device connections
 */
export async function getDeviceConnections(
  userId: string,
): Promise<DeviceConnection[]> {

  try {
    const { data, error } = await supabase
      .from('device_connections')
      .select('*, encryption_devices!linked_device_id(user_id)')
      .eq('encryption_devices.user_id', userId);

    if (error) {
      logger.error('Failed to get device connections', error);
      throw new Error(`Failed to get device connections: ${error.message}`);
    }

    const connections = (data || []).map(mapConnectionFromDB);
    return connections;
  } catch (error) {
    logger.error('Failed to get device connections', error as Error);
    throw error;
  }
}

/**
 * Update last synced timestamp for a device
 *
 * @param deviceId - Device ID
 */
export async function updateDeviceLastSynced(deviceId: string): Promise<void> {
  try {
    await supabase
      .from('encryption_devices')
      .update({ last_synced_at: new Date().toISOString() })
      .eq('id', deviceId);
  } catch (error) {
    logger.error('Failed to update device last synced', error as Error);
    // Don't throw - this is not critical
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Map database record to Device interface
 */

const readString = (
  value: Record<string, unknown>,
  key: string,
): string | undefined =>
  typeof value[key] === 'string' ? value[key] : undefined;

const readNullableString = (
  value: Record<string, unknown>,
  key: string,
): string | null => {
  const field = value[key];
  if (field === null) {
    return null;
  }
  return typeof field === 'string' ? field : null;
};

const toDeviceType = (value: unknown): DeviceType =>
  value === 'linked' ? 'linked' : 'primary';

const toDeviceStatus = (value: unknown): DeviceStatus => {
  if (value === 'inactive' || value === 'revoked') {
    return value;
  }
  return 'active';
};

const parseDeviceInfo = (value: unknown): Device['deviceInfo'] => {
  if (!isRecord(value)) {
    return {
      platform: 'unknown',
      browser: 'unknown',
    };
  }

  return {
    platform: readString(value, 'platform') || 'unknown',
    browser: readString(value, 'browser') || 'unknown',
    userAgent: readString(value, 'userAgent'),
  };
};

const toConnectionStatus = (
  value: unknown,
): DeviceConnection['connectionStatus'] => {
  if (value === 'paused' || value === 'disconnected') {
    return value;
  }
  return 'active';
};

function mapDeviceFromDB(record: unknown): Device {
  const row = isRecord(record) ? record : {};

  return {
    id: readString(row, 'id') || '',
    userId: readString(row, 'user_id') || '',
    deviceType: toDeviceType(row.device_type),
    deviceName: readString(row, 'device_name') || 'Unknown Device',
    deviceInfo: parseDeviceInfo(row.device_info),
    credentialId: readString(row, 'credential_id'),
    status: toDeviceStatus(row.status),
    lastSyncedAt: readNullableString(row, 'last_synced_at'),
    createdAt: readString(row, 'created_at') || new Date().toISOString(),
    updatedAt: readString(row, 'updated_at') || new Date().toISOString(),
  };
}

/**
 * Map database record to DeviceConnection interface
 */
function mapConnectionFromDB(record: unknown): DeviceConnection {
  const row = isRecord(record) ? record : {};

  return {
    primaryDeviceId: readString(row, 'primary_device_id') || '',
    linkedDeviceId: readString(row, 'linked_device_id') || '',
    connectionStatus: toConnectionStatus(row.connection_status),
    establishedAt:
      readString(row, 'established_at') ||
      readString(row, 'created_at') ||
      new Date().toISOString(),
  };
}

/**
 * Check if user has any primary devices
 *
 * @param userId - User ID
 * @returns True if user has at least one active primary device
 */
export async function hasPrimaryDevice(userId: string): Promise<boolean> {
  try {
    const { count, error } = await supabase
      .from('encryption_devices')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('device_type', 'primary')
      .eq('status', 'active')
      .limit(1);

    if (error) {
      logger.error('Failed to check for primary device', error);
      return false;
    }

    const hasPrimary = (count ?? 0) > 0;
    return hasPrimary;
  } catch (error) {
    logger.error('Failed to check for primary device', error as Error);
    return false;
  }
}
